import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import type { ApprovalStatus } from "@hisense/shared";
import { translateStatus } from "@/domain/i18n";
import { cn } from "@/lib/cn";

export const APPROVAL_FILTER_VALUES = [
  "all",
  "Approved",
  "Pending",
  "Under Review",
  "Rejected",
] as const;
export type ApprovalFilterValue = (typeof APPROVAL_FILTER_VALUES)[number];

const DOT_CLASS: Record<ApprovalFilterValue, string> = {
  all: "bg-ink-faint",
  Approved: "bg-ok",
  Pending: "bg-warn",
  "Under Review": "bg-info",
  Rejected: "bg-danger",
};

function optionLabel(
  value: ApprovalFilterValue,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  return value === "all" ? t("products.allApprovals") : translateStatus(t, value as ApprovalStatus);
}

export function ApprovalFilter({
  value,
  onChange,
}: {
  value: ApprovalFilterValue;
  onChange: (value: ApprovalFilterValue) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = APPROVAL_FILTER_VALUES.indexOf(value);

  const focusOption = (index: number) => {
    window.requestAnimationFrame(() => optionRefs.current[index]?.focus());
  };

  const openAndFocus = (index = selectedIndex) => {
    setOpen(true);
    focusOption(Math.max(0, index));
  };

  const closeAndFocusTrigger = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const select = (nextValue: ApprovalFilterValue) => {
    onChange(nextValue);
    closeAndFocusTrigger();
  };

  const onOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeAndFocusTrigger();
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }

    let nextIndex: number | null = null;
    if (event.key === "ArrowDown") nextIndex = (index + 1) % APPROVAL_FILTER_VALUES.length;
    if (event.key === "ArrowUp") nextIndex = (index - 1 + APPROVAL_FILTER_VALUES.length) % APPROVAL_FILTER_VALUES.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = APPROVAL_FILTER_VALUES.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    focusOption(nextIndex);
  };

  return (
    <div ref={rootRef} className={cn("relative w-48", open && "z-50")}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t("products.approval")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="product-approval-filter-list"
        onClick={() => (open ? setOpen(false) : openAndFocus())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openAndFocus(
              event.key === "ArrowDown"
                ? Math.min(selectedIndex + 1, APPROVAL_FILTER_VALUES.length - 1)
                : Math.max(selectedIndex - 1, 0),
            );
          }
          if (event.key === "Escape" && open) {
            event.preventDefault();
            setOpen(false);
          }
        }}
        className={cn(
          "flex h-9 w-full items-center gap-2.5 rounded-sm border border-line-strong bg-surface pl-3 pr-2.5 text-left text-sm text-ink",
          "transition-colors hover:border-ink-faint focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-brand)]/15",
        )}
      >
        <span aria-hidden="true" className={cn("size-2.5 shrink-0 rounded-full", DOT_CLASS[value])} />
        <span className="min-w-0 flex-1 truncate">{optionLabel(value, t)}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn("size-4 shrink-0 text-ink-faint transition-transform", open && "rotate-180")}
        />
      </button>

      {open ? (
        <div
          id="product-approval-filter-list"
          role="listbox"
          aria-label={t("products.approval")}
          className="absolute left-0 top-[calc(100%+0.375rem)] z-50 w-56 overflow-hidden rounded-md border border-line bg-surface p-1 shadow-[var(--shadow-pop)]"
        >
          {APPROVAL_FILTER_VALUES.map((option, index) => {
            const selected = option === value;
            return (
              <button
                key={option}
                ref={(node) => { optionRefs.current[index] = node; }}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => select(option)}
                onKeyDown={(event) => onOptionKeyDown(event, index)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-sm px-2.5 py-2 text-left text-sm transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-bright",
                  selected ? "bg-brand-tint text-brand-ink" : "text-ink-soft hover:bg-sunken hover:text-ink",
                )}
              >
                <span aria-hidden="true" className={cn("size-2.5 shrink-0 rounded-full", DOT_CLASS[option])} />
                <span className="min-w-0 flex-1 truncate">{optionLabel(option, t)}</span>
                {selected ? <Check aria-hidden="true" className="size-4 shrink-0 text-brand" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
